import { MetricInfoDefinition } from '../components/MasterAdmin/ui/MetricInfoPopover';

type MetricInfoMap = Record<string, MetricInfoDefinition>;

export const userDashboardMetricInfo: MetricInfoMap = {
  totalRegisteredUsers: {
    definition: 'Displays the cumulative count of unique registered users. The percentage value next to it (e.g., +21%) represents the user growth rate during the selected date range compared to the preceding period.',
    formula: '• Total Registered Users = COUNT(DISTINCT Users.userId)\n• Percentage Growth next to it = ((Total Users - Baseline Users Before Range) / Baseline Users Before Range) * 100',
    logic: [
      '1. Total Users: Counts all records in MySQL "Users" table. Joins "user_fcm_tokens" table to filter by platform (Android, iOS, or Web) if selected.',
      '2. Baseline Users Before Range: Sums the registrations that occurred inside the selected date range, and subtracts this sum from the current total registered count: Baseline = Total - RegistrationsInRange.',
      '3. Growth Rate Calculation: Divides the new registrations inside the selected date range by this baseline value, showing how much your user base grew (in %) during the selected period.',
    ],
    sources: [
      { system: 'MySQL', table: 'Users', columns: ['userId', 'createdAt'] },
      { system: 'MySQL', table: 'user_fcm_tokens', columns: ['userId', 'platform'] },
    ],
  },
  churnRisk: {
    definition: 'Identifies registered members who have been dormant (zero app opens or service activity) for 15 or 30 consecutive days. Allows executives to trigger targeted re-engagement notifications.',
    formula: '• Churn Risk Count = COUNT(Users where lastActiveAt < 30 Days Ago OR (lastActiveAt IS NULL AND createdAt < 30 Days Ago))',
    logic: [
      '1. Inactivity Threshold: Detects accounts where the patient has not opened the mobile app or logged a session in the selected window (15 Days or 30 Days).',
      '2. Dormant Accounts: Includes registered users whose last activity timestamp is older than 30 days, as well as accounts created >30 days ago with zero session history.',
      '3. "Anonymous User" Label: Represents real registered patients who signed up via phone OTP but skipped entering their name. (Not guest traffic).',
      '4. Re-Engagement Action: Click this card to view the At-Risk Patient List and dispatch 1-click push notifications or WhatsApp campaigns.',
    ],
    sources: [
      { system: 'MySQL Database', table: 'Users', columns: ['userId', 'name', 'phone_number', 'emailId', 'lastActiveAt', 'createdAt'] },
    ],
  },
  appStickiness: {
    definition: 'Executive Retention Metric: Measures what percentage of your registered monthly active user base returns to launch the app or interact with Vedika Health every single day.',
    formula: '• Stickiness Ratio = (Daily Active Users / Monthly Active Users) * 100\n• Example: (2 DAU / 6 MAU) = 33.3%\n• Growth Badge (e.g. +32%) = Percentage growth or decline in user stickiness vs last month.',
    logic: [
      '1. Product-Market Fit Indicator: Shows whether the app is becoming a daily habit for your members or just a one-time download.',
      '2. Benchmark Standards: Under 10% indicates sporadic single-use; 15%-20% is healthy healthcare app engagement; above 25% indicates strong daily habit formation.',
      '3. Raw Calculation: Displayed directly on the card as "(2 DAU / 6 MAU)" next to the main percentage.',
      '4. High-Performance Source: Pre-computed directly from BigQuery user_features_daily sparse digital twins.',
    ],
    sources: [
      { system: 'BigQuery', table: 'user_features_daily', columns: ['user_id', 'feature_date', 'is_active_today'] },
    ],
  },
  activeUsers30d: {
    definition: 'Monthly Active Users (MAU): Counts the total number of unique registered members who launched the app or engaged in health interactions over the last 30 rolling days.',
    formula: '• MAU Count = COUNT(DISTINCT user_id) over the past 30 rolling days\n• Growth Badge (e.g. -50%) = Percentage growth or decline in active monthly customers vs preceding 30-day window.',
    logic: [
      '1. Total Active Customer Base: Represents your total monthly active reach across all platforms (Android, iOS, Web).',
      '2. Stable Benchmark: Reflects a rolling 30-day baseline and remains fixed regardless of custom date range chart filters.',
      '3. High-Performance Source: Calculated directly from BigQuery user_features_daily sparse digital twins.',
    ],
    sources: [
      { system: 'BigQuery', table: 'user_features_daily', columns: ['user_id', 'feature_date', 'is_active_30d'] },
    ],
  },
  newUsersToday: {
    definition: 'Counts new patient accounts registered on Vedika since 12:00 AM today.',
    formula: '• New Users Today = COUNT(Users registered since 12:00 AM Today)\n• Growth Badge = % Change in today\'s sign-ups vs yesterday\'s sign-ups',
    logic: [
      '1. Midnight Reset: Counts all new user registrations created between 12:00 AM today and the current time.',
      '2. Daily Velocity: Compares today\'s new sign-up volume against total new sign-ups completed yesterday.',
      '3. Multi-Platform Filtering: Respects the selected platform filter (Android, iOS, or Web).',
    ],
    sources: [
      { system: 'MySQL', table: 'Users', columns: ['userId', 'createdAt'] },
      { system: 'MySQL', table: 'user_fcm_tokens', columns: ['userId', 'platform'] },
    ],
  },
  newUsersThisWeek: {
    definition: 'Counts new patient accounts registered during the current calendar week (Monday to Sunday).',
    formula: '• New Users This Week = COUNT(Users registered since Monday 12:00 AM)\n• Growth Badge = % Change in this week\'s sign-ups vs previous week\'s sign-ups',
    logic: [
      '1. Weekly Cohort: Sums all new user registrations completed from Monday 12:00 AM through today.',
      '2. Weekly Growth Comparison: Compares current week sign-up volume against total new sign-ups during the preceding calendar week.',
      '3. Multi-Platform Filtering: Respects the selected platform filter (Android, iOS, or Web).',
    ],
    sources: [
      { system: 'MySQL', table: 'Users', columns: ['userId', 'createdAt'] },
      { system: 'MySQL', table: 'user_fcm_tokens', columns: ['userId', 'platform'] },
    ],
  },
  blockedUsers: {
    definition: 'Counts registered members whose accounts have been disabled or blocked by the Master Admin.',
    formula: '• Blocked Users Count = COUNT(Users where status = 0)',
    logic: [
      '1. Admin Access Control: Counts accounts where status is set to disabled (status = 0 / false).',
      '2. Immediate Session Revocation: Blocked users have active JWT login tokens revoked and FCM push notifications disabled.',
      '3. Platform Filtering: Respects the selected platform filter (Android, iOS, or Web).',
    ],
    sources: [
      { system: 'MySQL', table: 'Users', columns: ['userId', 'status'] },
      { system: 'MySQL', table: 'user_fcm_tokens', columns: ['userId', 'platform'] },
    ],
  },
  websiteVisitors: {
    definition: 'Counts total cumulative pageviews and visitor sessions tracked on the main Vedika Health landing website.',
    formula: '• Website Visitors = Live counter of total landing page sessions',
    logic: [
      '1. Real-Time Tracking: Increments automatically whenever a new prospect lands on the main website (vedika.health).',
      '2. Public Reach Meter: Measures overall marketing campaign reach and public brand traffic.',
      '3. High-Speed Source: Streamed live from Firebase Firestore global counter document.',
    ],
    sources: [
      { system: 'Firestore', table: 'site_visitor_counts/global', columns: ['total', 'updatedAt'] },
    ],
  },
  userGrowthRegistrations: {
    definition: 'Displays the daily or weekly registration trend. The chart automatically adjusts depending on the selected filters.',
    formula: 'Filtered by: Selected Dates, Selected Platform, and Interval (Daily / Weekly).',
    logic: [
      'Queries MySQL user registrations inside the selected date range.',
      'Groups data by day if the range is 31 days or less; groups by week if the range is larger.',
      'Filters by the selected platform.',
    ],
    sources: [
      { system: 'MySQL', table: 'Users', columns: ['userId', 'createdAt'] },
      { system: 'MySQL', table: 'user_fcm_tokens', columns: ['userId', 'platform'] },
    ],
  },
  userServiceUsage: {
    definition: 'Displays the total volume of healthcare services used by patients on the app (ambulance bookings and doctor consultations) over the selected date range.',
    formula: '• Doctor Consultations Total = COUNT(ClinicAppointments created in range)\n• Ambulance Bookings Total = COUNT(AmbulanceBookings created in range)',
    logic: [
      '1. Live Real-Time Counts: Queries MySQL transactional tables directly to guarantee 100% data accuracy.',
      '2. Doctor Consultations (e.g. 22): Sums all clinic appointments created inside the selected date range.',
      '3. Ambulance Bookings (e.g. 46): Sums all ambulance booking requests created inside the selected date range.',
      '4. Timeline Trend Graph: Groups bookings by date to display daily booking volume curves.',
    ],
    sources: [
      { system: 'MySQL Database', table: 'ClinicAppointments', columns: ['id', 'createdAt'] },
      { system: 'MySQL Database', table: 'AmbulanceBookings', columns: ['id', 'createdAt'] },
    ],
  },
  topUserLocations: {
    definition: 'A ranked list of the top 5 cities based on either: 1) where most of your registered users live, or 2) where most of the orders and bookings are coming from.',
    formula: 'Filtered by: Selected Dates, Selected Platform.',
    logic: [
      'Top Cities by Users: Groups registered user profiles by city (MySQL).',
      'Top Cities by Orders: Groups daily orders and bookings by user city (BigQuery).',
    ],
    sources: [
      { system: 'MySQL', table: 'Users', columns: ['userId', 'city'] },
      { system: 'BigQuery', table: 'user_features_daily', columns: ['city'] },
    ],
  },
  ecosystemFeatureAdoption: {
    definition: 'Provides executive high-level pulse metrics across Vedika’s core healthcare modules (Health Blogs, Digital Health Storage, Vedika AI Assistant, Her Phases, and Health Awareness Tools) with 1-click navigation to specialized analytics pages.',
    formula: 'Top-Level Active Counts & Growth Trends (▲ +15%) per module.',
    logic: [
      '1. Health Blogs: Active readers & article views in range. Click routes to Content Analytics.',
      '2. Digital Health Storage: Total MB stored & patients saving records. Click routes to Health Record Usage.',
      '3. Vedika AI Assistant: Active AI users & resolution rate. Click routes to Chatbot Intelligence.',
      '4. Her Phases: Active female patients tracking cycles & wellness. Click routes to Patient Directory.',
      '5. Health Awareness: Total interactive health tool engagements. Click routes to Analytics Dashboard.',
    ],
    sources: [
      { system: 'BigQuery', table: 'item_metrics_daily', columns: ['target_type', 'target_id', 'views', 'unique_users'] },
      { system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'event_name', 'user_id', 'timestamp'] },
    ],
  },
  userActivityToday: {
    definition: 'Provides executive high-level pulse metrics across Vedika’s core healthcare modules (Health Blogs, Digital Health Storage, Vedika AI Assistant, Her Phases, and Health Awareness Tools) with 1-click navigation to specialized analytics pages.',
    formula: 'Top-Level Active Counts & Growth Trends per module.',
    logic: [
      'Displays high-level active counts for all 5 core Vedika modules with 1-click deep-dive navigation.',
    ],
    sources: [
      { system: 'BigQuery', table: 'item_metrics_daily', columns: ['target_type', 'target_id', 'views', 'unique_users'] },
    ],
  },
  userRetention: {
    definition: 'Measures patient loyalty & habit formation by tracking what percentage of users return to the app after their initial signup date. Essential for evaluating product stickiness against Healthcare Industry Benchmarks.',
    formula: '• Cohort Retention % = (Returned Patients in Window / Total Cohort Patients) × 100%\n• 1-Day Benchmark: 25% | 7-Day Benchmark: 35% | 30-Day Benchmark: 20%',
    logic: [
      '1. 1-Day Retention: % of patients active yesterday who returned to open the app today (Initial Hook & First Impression).',
      '2. 7-Day Retention: % of patients active 7 days ago who returned this week (Weekly Habit & Prescription Refills).',
      '3. 30-Day Retention: % of patients active 30 days ago who remain active today (Long-term Retention & Low Churn).',
      '4. Returning Users Ratio: Ratio of repeat active patients vs brand-new signups today.',
      '5. Management Action: Use Push Notification campaigns to re-engage patients falling below the 20% 30-day target.',
    ],
    sources: [
      { system: 'BigQuery', table: 'user_features_daily', columns: ['user_id', 'cohort_date', 'feature_date', 'is_active_today', 'is_active_7d', 'is_active_30d'] },
      { system: 'MySQL Database', table: 'Users', columns: ['userId', 'createdAt', 'lastActiveAt'] },
    ],
  },
  dau: {
    definition: 'Daily Active Users (DAU) metric shown in the Active Users Analytics block.',
    formula: 'COUNT(DISTINCT user_id)',
    logic: ['Same DAU logic as Active Users Today: distinct analytics.events.user_id for the current date.'],
    sources: [
      { system: 'BigQuery', table: 'analytics.events', columns: ['user_id', 'timestamp', 'platform'] },
    ],
  },
  wau: {
    definition: 'Weekly Active Users (WAU) metric shown in the Active Users Analytics block.',
    formula: 'COUNT(DISTINCT user_id)',
    logic: ['Same WAU logic as Active Users (Last 7 Days): distinct analytics.events.user_id across the last 7 days.'],
    sources: [
      { system: 'BigQuery', table: 'analytics.events', columns: ['user_id', 'timestamp', 'platform'] },
    ],
  },
  mau: {
    definition: 'Monthly Active Users (MAU) metric shown in the Active Users Analytics block.',
    formula: 'COUNT(DISTINCT user_id)',
    logic: ['Same MAU logic as Active Users (Last 30 Days): distinct analytics.events.user_id across the last 30 days.'],
    sources: [
      { system: 'BigQuery', table: 'analytics.events', columns: ['user_id', 'timestamp', 'platform'] },
    ],
  },
  avgSessionsPerUser: {
    definition: 'Average number of sessions per distinct user over the last 30 days.',
    formula: 'total_sessions / distinct_users',
    logic: [
      'Reads COUNT(*) as total_sessions from analytics.sessions.',
      'Reads COUNT(DISTINCT user_id) as distinct_users from analytics.sessions.',
      'Computes total_sessions / distinct_users in backend code.',
    ],
    sources: [
      { system: 'BigQuery', table: 'analytics.sessions', columns: ['user_id', 'session_start', 'device'] },
    ],
  },
  avgSessionDurationSec: {
    definition: 'Average session duration in seconds over the last 30 days.',
    formula: 'AVG(session_duration_sec)',
    logic: [
      'Uses AVG(session_duration_sec) from analytics.sessions.',
      'Filters sessions where DATE(session_start) >= DATE_SUB(CURRENT_DATE(), INTERVAL 29 DAY).',
    ],
    sources: [
      { system: 'BigQuery', table: 'analytics.sessions', columns: ['session_duration_sec', 'session_start', 'device'] },
    ],
  },
  activeAndroidUsers: {
    definition: 'Distinct active users having at least one active Android FCM token.',
    formula: 'COUNT(DISTINCT userId) WHERE platform = "android"',
    logic: [
      'Finds user_fcm_tokens rows where platform = "android" and isActive = true.',
      'Joins to Users where status = true.',
      'Deduplicates by userId in backend code because one user can have multiple tokens.',
    ],
    sources: [
      { system: 'MySQL', table: 'user_fcm_tokens', columns: ['userId', 'platform', 'isActive'] },
      { system: 'MySQL', table: 'Users', columns: ['userId', 'status'] },
    ],
  },
  activeIOSUsers: {
    definition: 'Distinct active users having at least one active iOS FCM token.',
    formula: 'COUNT(DISTINCT userId) WHERE platform = "ios"',
    logic: [
      'Finds user_fcm_tokens rows where platform = "ios" and isActive = true.',
      'Joins to Users where status = true.',
      'Deduplicates by userId in backend code because one user can have multiple tokens.',
    ],
    sources: [
      { system: 'MySQL', table: 'user_fcm_tokens', columns: ['userId', 'platform', 'isActive'] },
      { system: 'MySQL', table: 'Users', columns: ['userId', 'status'] },
    ],
  },
};

export const vendorDashboardMetricInfo: MetricInfoMap = {
  totalRegisteredVendors: {
    definition: 'Total vendor records registered across all vendor categories.',
    formula: 'COUNT(*)',
    logic: ['Counts all rows in the Vendor table.'],
    sources: [
      { system: 'MySQL', table: 'Vendor', columns: ['vendorId'] },
    ],
  },
  totalBookings: {
    definition: 'Total bookings, appointments, and orders across all service verticals.',
    formula: 'SUM(AmbulanceBookings + ClinicAppointments + MedicineOrders + BloodBankBookings + LabTestBookings + BedBookings + ProductOrders)',
    logic: [
      'Counts all records from AmbulanceBookings, ClinicAppointments, MedicineOrders, BloodBankBookings, LabTestBookings, BedBookings, and ProductOrders operational tables.',
    ],
    sources: [
      { system: 'MySQL', table: 'AmbulanceBookings', columns: ['bookingId'] },
      { system: 'MySQL', table: 'ClinicAppointments', columns: ['clinicAppointmentId'] },
      { system: 'MySQL', table: 'MedicineOrders', columns: ['orderId'] },
      { system: 'MySQL', table: 'BloodBankBookings', columns: ['bookingId'] },
      { system: 'MySQL', table: 'LabTestBookings', columns: ['bookingId'] },
      { system: 'MySQL', table: 'BedBookings', columns: ['bedBookingId'] },
      { system: 'MySQL', table: 'ProductOrders', columns: ['orderId'] },
    ],
  },
  activeVendorSessions: {
    definition: 'Vendor authentication sessions currently in ACTIVE status.',
    formula: 'COUNTIF(status = "ACTIVE")',
    logic: [
      'Counts VendorAuthSession rows where status = "ACTIVE".',
    ],
    sources: [
      { system: 'MySQL', table: 'VendorAuthSession', columns: ['id', 'status'] },
    ],
  },
  activeVendorTokens: {
    definition: 'Active vendor FCM token records.',
    formula: 'COUNTIF(isActive = true)',
    logic: [
      'Counts VendorFcmToken rows where isActive = true.',
    ],
    sources: [
      { system: 'MySQL', table: 'VendorFcmToken', columns: ['id', 'isActive', 'platform'] },
    ],
  },
  vendorCategories: {
    definition: 'Displays the number of registered and active vendor accounts grouped by their service vertical (Hospital, Clinic, Medical Store, etc.).',
    formula: 'COUNT(vendorId) GROUP BY vendorRole',
    logic: [
      'Groups Vendor.vendorRole and counts total vendors per role.',
      'Sums active vendors using CASE WHEN isActive = true THEN 1 ELSE 0 END.',
      'Active indicates the vendor account is approved and enabled in the database (isActive = true). It does not represent whether a vendor is currently online or logged in.',
      'Note: Delivery Partners (role ID 7) are included in these categories, ensuring the sum of category totals matches the Total Registered Vendors count.',
    ],
    sources: [
      { system: 'MySQL', table: 'Vendor', columns: ['vendorId', 'vendorRole', 'isActive'] },
    ],
  },
  performanceOverview: {
    definition: 'Time-series chart showing total booking transactions and net revenue trends, filterable by specific healthcare service verticals.',
    formula: 'COUNT(ledgerId) & SUM(netPayable) GROUP BY period, serviceType',
    logic: [
      'Bookings: Computed from the count of normal ledger entries in the VendorSettlementLedger table, optionally filtered by the selected service type.',
      'Revenue: Aggregated from the netPayable column in the VendorSettlementLedger table (representing final vendor payout amounts), naturally adjusting for adjustments and refunds.',
      'Filters: Can be narrowed down to specific services (Ambulance, Clinic, Medical Store, etc.) to analyze line-of-business trends over daily, weekly, monthly, or quarterly periods.',
    ],
    sources: [
      { system: 'MySQL', table: 'VendorSettlementLedger', columns: ['netPayable', 'serviceCompletedAt', 'entryType', 'ledgerId', 'serviceType'] },
    ],
  },
  demographics: {
    definition: 'Displays user demographics metrics (bookings count and revenue) grouped by age groups, gender, and chronic health conditions.',
    formula: 'SUM(booking.amount) GROUP BY user.ageGroup / user.gender / user.healthCondition',
    logic: [
      'Correlates successful transactions from the 6 major booking tables back to their respective Users dateOfBirth and gender values.',
      'Extracts health conditions by checking MedicalProfile isDiabetic flag and parsing chronicConditions JSON array contents for Heart Disease, Respiratory, and Joint Pain keywords.',
    ],
    sources: [
      { system: 'MySQL', table: 'Users', columns: ['dateOfBirth', 'gender'] },
      { system: 'MySQL', table: 'MedicalProfiles', columns: ['isDiabetic', 'chronicConditions'] },
      { system: 'MySQL', table: 'AmbulanceBookings', columns: ['userId', 'totalPayableAmount'] },
      { system: 'MySQL', table: 'ClinicAppointments', columns: ['userId', 'paidAmount'] },
      { system: 'MySQL', table: 'MedicineOrders', columns: ['userId', 'totalAmount'] },
      { system: 'MySQL', table: 'BloodBankBookings', columns: ['userId', 'totalAmount'] },
      { system: 'MySQL', table: 'LabTestBookings', columns: ['userId', 'totalAmount'] },
      { system: 'MySQL', table: 'BedBookings', columns: ['userId', 'paidAmount'] },
    ],
  },
  topPerformingVendors: {
    definition: 'Top performing vendors list sorted descending by total settlement revenue.',
    formula: 'SUM(VendorSettlementLedger.netPayable) GROUP BY vendorId ORDER BY revenue DESC LIMIT 5',
    logic: [
      'Aggregates total successful bookings counts and sums netPayable from the VendorSettlementLedger table for each active vendor.',
      'Resolves vendor credentials (name, category, and city location) by dynamically querying their category profile tables (Hospitals, DoctorClinics, VendorMedicalStoreProfiles, AmbulanceAgencyProfiles, BloodBankAgencies, DiagnosticCenters, and ProductPartners).',
      'The UI lets you filter this ranked list dynamically by service category.',
    ],
    sources: [
      { system: 'MySQL', table: 'VendorSettlementLedger', columns: ['vendorId', 'netPayable'] },
      { system: 'MySQL', table: 'Vendors', columns: ['vendorId', 'vendorRole'] },
      { system: 'MySQL', table: 'Hospitals / DoctorClinics / VendorMedicalStoreProfiles / AmbulanceAgencyProfiles / BloodBankAgencies / DiagnosticCenters / ProductPartners', columns: ['name', 'agencyName', 'doctorName', 'clinicName', 'city'] },
    ],
  },
};

export const ambulanceOffHoursMetricInfo: MetricInfoMap = {
  totalClicks: {
    definition: 'Total raw clicks on "Call Ambulance" during off-hours (9:00 PM – 9:00 AM).',
    formula: 'COUNT(*)',
    logic: [
      'Counts all events in BigQuery where event_type = "HOME_AMBULANCE_OFF_HOURS_CLICK".',
      'Queries the selected day range (default: last 90 days).'
    ],
    sources: [
      { system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'timestamp'] }
    ]
  },
  androidClicks: {
    definition: 'Ambulance off-hours clicks originating from the Android app.',
    formula: 'COUNTIF(platform = "ANDROID")',
    logic: [
      'Counts rows where event_type = "HOME_AMBULANCE_OFF_HOURS_CLICK" and platform is Android.'
    ],
    sources: [
      { system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'platform', 'timestamp'] }
    ]
  },
  iosClicks: {
    definition: 'Ambulance off-hours clicks originating from the iOS app.',
    formula: 'COUNTIF(platform = "IOS")',
    logic: [
      'Counts rows where event_type = "HOME_AMBULANCE_OFF_HOURS_CLICK" and platform is iOS.'
    ],
    sources: [
      { system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'platform', 'timestamp'] }
    ]
  },
  webClicks: {
    definition: 'Ambulance off-hours clicks originating from the Web platform.',
    formula: 'COUNTIF(platform = "WEB")',
    logic: [
      'Counts rows where event_type = "HOME_AMBULANCE_OFF_HOURS_CLICK" and platform is Web.'
    ],
    sources: [
      { system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'platform', 'timestamp'] }
    ]
  },
  uniqueUsers: {
    definition: 'Number of unique registered or anonymous users clicking "Call Ambulance" in off-hours.',
    formula: 'COUNT(DISTINCT user_id)',
    logic: [
      'Groups the BigQuery analytics events by user_id and counts the total unique user IDs in the list.'
    ],
    sources: [
      { system: 'BigQuery', table: 'analytics.events', columns: ['user_id', 'event_type', 'timestamp'] }
    ]
  }
};

export const healthRecordUsageMetricInfo: MetricInfoMap = {
  totalRecords: {
    definition: 'Total number of active health records uploaded by all users across the platform, calculated from storage upload logs.',
    formula: 'COUNT(storage_path) WHERE entity_type = "HEALTH_RECORD" AND latest_operation = "UPLOAD"',
    logic: [
      'Partitions storage events by storage_path and orders by event timestamp descending.',
      'Filters for paths where the latest operation is UPLOAD and status is SUCCESS.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['storage_path', 'operation', 'status', 'entity_type'] }],
  },
  storageUsage: {
    definition: 'Total binary storage space consumed by all active (non-deleted) health records.',
    formula: 'SUM(file_size_bytes) WHERE entity_type = "HEALTH_RECORD" AND latest_operation = "UPLOAD"',
    logic: [
      'Sums the file_size_bytes of all storage paths whose latest event is a successful UPLOAD.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['file_size_bytes', 'storage_path', 'operation', 'status', 'entity_type'] }],
  },
  usersWithRecords: {
    definition: 'Count of unique registered users who have at least one active health record in storage.',
    formula: 'COUNT(DISTINCT actor_id) WHERE entity_type = "HEALTH_RECORD" AND latest_operation = "UPLOAD"',
    logic: [
      'Counts unique actor_id values from all storage paths whose latest event is a successful UPLOAD.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['actor_id', 'storage_path', 'operation', 'status', 'entity_type'] }],
  },
  usersWithoutRecords: {
    definition: 'Count of registered users who have no active health records in storage.',
    formula: 'totalUsers (MySQL) - usersWithRecords (BigQuery)',
    logic: [
      'Queries total registered users from MySQL Users table.',
      'Finds unique actor_id values of users with records in BigQuery storage_upload_events.',
      'Subtracts the BigQuery user count from total users.',
    ],
    sources: [
      { system: 'MySQL', table: 'Users', columns: ['userId'] },
      { system: 'BigQuery', table: 'storage_upload_events', columns: ['actor_id', 'entity_type'] },
    ],
  },
  uploadSuccessRate: {
    definition: 'Percentage of health record upload attempts that succeeded.',
    formula: 'COUNTIF(status = "SUCCESS") / COUNT(*) * 100',
    logic: [
      'Calculates the ratio of successful UPLOAD events to total UPLOAD events for HEALTH_RECORD entity type.',
    ],
    sources: [
      { system: 'BigQuery', table: 'storage_upload_events', columns: ['status', 'operation', 'entity_type'] },
    ],
  },
  avgUploadLatency: {
    definition: 'Average time taken to upload a health record file to Firebase storage.',
    formula: 'AVG(duration_ms) WHERE operation = "UPLOAD" AND entity_type = "HEALTH_RECORD"',
    logic: [
      'Aggregates the duration_ms column for all successful and failed UPLOAD events of HEALTH_RECORD entity type.',
    ],
    sources: [
      { system: 'BigQuery', table: 'storage_upload_events', columns: ['duration_ms', 'operation', 'entity_type'] },
    ],
  },
  recordTypesDistribution: {
    definition: 'Donut chart visualizing the percentage distribution of total active health records storage size (in bytes) across different document types:\n\n' +
      '• PRESCRIPTION: Doctor prescriptions uploaded by the user to their health locker.\n' +
      '• TEST_REPORT: General medical laboratory reports or diagnostic files.\n' +
      '• MEDICAL_BILL: Pharmacy bills, hospital receipts, or medical invoices.\n' +
      '• MEDICLAIM_POLICY: Health insurance policy documents or health cards.\n' +
      '• VACCINE_HISTORY: Vaccination certificates, records, or timeline documents.\n' +
      '• MEDICLAIM: Health insurance claim forms or pre-authorizations.\n' +
      '• Lab Report: Laboratory diagnostic test results.',
    formula: 'SUM(file_size_bytes) GROUP BY JSON_VALUE(metadata_json, "$.type")',
    logic: [
      'Filters for all active health records in storage (latest event is UPLOAD).',
      'Groups by category type (PRESCRIPTION, TEST_REPORT, MEDICAL_BILL, etc.).',
      'Calculates size share of each category out of total bytes.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['file_size_bytes', 'metadata_json', 'entity_type', 'storage_path', 'operation', 'status'] }],
  },
  typeBreakdownWeights: {
    definition: 'Detailed tabular breakdown and visual progress bars showing file counts, storage sizes, and storage share percentage for each health record category:\n\n' +
      '• PRESCRIPTION: Doctor prescriptions uploaded by the user to their health locker.\n' +
      '• TEST_REPORT: General medical laboratory reports or diagnostic files.\n' +
      '• MEDICAL_BILL: Pharmacy bills, hospital receipts, or medical invoices.\n' +
      '• MEDICLAIM_POLICY: Health insurance policy documents or health cards.\n' +
      '• VACCINE_HISTORY: Vaccination certificates, records, or timeline documents.\n' +
      '• MEDICLAIM: Health insurance claim forms or pre-authorizations.\n' +
      '• Lab Report: Laboratory diagnostic test results.',
    formula: 'COUNT(1) & SUM(file_size_bytes) GROUP BY JSON_VALUE(metadata_json, "$.type")',
    logic: [
      'Filters for all active health records in storage.',
      'Extracts file category type from metadata_json.',
      'Aggregates total counts and storage size bytes for each category type.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['file_size_bytes', 'metadata_json', 'entity_type', 'storage_path', 'operation', 'status'] }],
  },
  otherUserStorageDistribution: {
    definition: 'Donut chart visualizing the percentage distribution of storage size (in bytes) across other app-user-related documents:\n\n' +
      '• Profile Avatars: Display photos uploaded by users for their account avatar.\n' +
      '• Consultation Chat Files: Images, PDFs, and documents shared during doctor chats or video consults.\n' +
      '• Blood Request Prescriptions: Prescriptions uploaded when requesting blood units.\n' +
      '• Medicine Order Prescriptions: Doctor prescriptions uploaded to purchase medicines online.\n' +
      '• Lab Test Bookings: Prescriptions uploaded to schedule diagnostic laboratory tests.\n' +
      '• Lab Test Reports: Final diagnostic laboratory reports generated or uploaded for the patient.\n' +
      '• User Feedback/Support: Screenshots and attachments uploaded with feedback or grievances.\n' +
      '• Return Refund Claims: Photos and evidence uploaded to claim refunds or returns.',
    formula: 'SUM(file_size_bytes) GROUP BY entity_type',
    logic: [
      'Filters storage events for user-related entity types (USER_PROFILE_PHOTO, CHAT_MESSAGE_FILE, BLOOD_REQUEST_PRESCRIPTION, MEDICINE_PRESCRIPTION_UPLOAD, LAB_TEST_PRESCRIPTION, LAB_TEST_REPORT, FEEDBACK_ATTACHMENT, GRIEVANCE_ATTACHMENT, RETURN_REFUND_EVIDENCE).',
      'Filters for files whose latest event is UPLOAD and status is SUCCESS.',
      'Groups by entity_type to aggregate total bytes.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['file_size_bytes', 'entity_type', 'storage_path', 'operation', 'status'] }],
  },
  otherUserTypeBreakdownWeights: {
    definition: 'Detailed breakdown list and visual progress bars showing file counts, storage sizes, and storage share percentage for all other user file categories:\n\n' +
      '• Profile Avatars: Display photos uploaded by users for their account avatar.\n' +
      '• Consultation Chat Files: Images, PDFs, and documents shared during doctor chats or video consults.\n' +
      '• Blood Request Prescriptions: Prescriptions uploaded when requesting blood units.\n' +
      '• Medicine Order Prescriptions: Doctor prescriptions uploaded to purchase medicines online.\n' +
      '• Lab Test Bookings: Prescriptions uploaded to schedule diagnostic laboratory tests.\n' +
      '• Lab Test Reports: Final diagnostic laboratory reports generated or uploaded for the patient.\n' +
      '• User Feedback/Support: Screenshots and attachments uploaded with feedback or grievances.\n' +
      '• Return Refund Claims: Photos and evidence uploaded to claim refunds or returns.',
    formula: 'COUNT(1) & SUM(file_size_bytes) GROUP BY entity_type',
    logic: [
      'Filters storage events for other user-related entity types.',
      'Aggregates total file counts and size bytes for each category.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['file_size_bytes', 'entity_type', 'storage_path', 'operation', 'status'] }],
  },
};

export const reportsMetricInfo: MetricInfoMap = {
  page: {
    definition: 'System performance and application stability metrics across API traffic, latency, and exception volume.',
    formula: 'Aggregated from daily API and error summary tables over the selected date range.',
    logic: [
      'Loads api_metrics_daily and error_summary_daily for the selected window.',
      'Computes weighted KPIs and ranks top endpoints / error types for triage.',
    ],
    sources: [
      { system: 'BigQuery', table: 'api_metrics_daily', columns: ['date', 'endpoint', 'total_requests', 'error_rate', 'avg_response_time', 'p95_response_time'] },
      { system: 'BigQuery', table: 'error_summary_daily', columns: ['error_type', 'total_occurrences', 'unique_users'] },
    ],
  },
  totalApiCalls: {
    definition: 'Total incoming API requests in the selected range. Indicates server load and scaling needs.',
    formula: 'SUM(total_requests)',
    logic: ['No hard limit; auto-scaling enabled.', 'Higher volume without latency rise usually means healthy scaling.'],
    sources: [{ system: 'BigQuery', table: 'api_metrics_daily', columns: ['total_requests'] }],
  },
  overallErrorRate: {
    definition: 'Percentage of failed requests (4xx/5xx). Healthy rate is under 1%.',
    formula: 'SUM(total_requests * error_rate) / SUM(total_requests) * 100',
    logic: ['Healthy: < 1%', 'Warning: 0.5%–2%', 'Critical: > 2%'],
    sources: [{ system: 'BigQuery', table: 'api_metrics_daily', columns: ['total_requests', 'error_rate'] }],
  },
  avgResponseTime: {
    definition: 'Request-weighted average latency across all tracked endpoints.',
    formula: 'SUM(total_requests * avg_response_time) / SUM(total_requests)',
    logic: ['Target performance is < 200ms.'],
    sources: [{ system: 'BigQuery', table: 'api_metrics_daily', columns: ['total_requests', 'avg_response_time'] }],
  },
  p95ResponseTime: {
    definition: 'Experience of the slowest 5% of users — catches tail-latency issues.',
    formula: 'SUM(total_requests * p95_response_time) / SUM(total_requests)',
    logic: ['SLA target: < 500ms.', 'Watch for spikes above 400–800ms under load.'],
    sources: [{ system: 'BigQuery', table: 'api_metrics_daily', columns: ['total_requests', 'p95_response_time'] }],
  },
  totalExceptions: {
    definition: 'Count of application crashes, exceptions, or UI errors logged in the window.',
    formula: 'SUM(total_occurrences)',
    logic: ['Goal is 0.', 'Distinct from HTTP status-based API errors shown in other KPIs.'],
    sources: [{ system: 'BigQuery', table: 'error_summary_daily', columns: ['total_occurrences'] }],
  },
  usersImpacted: {
    definition: 'Count of unique users who faced at least one system error.',
    formula: 'SUM(unique_users)',
    logic: ['Goal is 0.', 'Helps prioritize fixes by blast radius.'],
    sources: [{ system: 'BigQuery', table: 'error_summary_daily', columns: ['unique_users'] }],
  },
  performanceTrends: {
    definition: 'Daily traffic volume vs average latency to correlate load spikes with slowdowns.',
    formula: 'Requests = SUM(total_requests) by date; Latency = weighted AVG(avg_response_time) by date',
    logic: ['Useful to spot whether latency rises with traffic or independently.'],
    sources: [{ system: 'BigQuery', table: 'api_metrics_daily', columns: ['date', 'total_requests', 'avg_response_time'] }],
  },
  topEndpoints: {
    definition: 'Top 10 most active API endpoints by request volume, with error rate and latency.',
    formula: 'GROUP BY endpoint ORDER BY SUM(total_requests) DESC LIMIT 10',
    logic: [
      'Error % = failed requests / all requests * 100',
      'Avg / P95 latency are request-weighted across dates.',
    ],
    sources: [{ system: 'BigQuery', table: 'api_metrics_daily', columns: ['endpoint', 'total_requests', 'error_rate', 'avg_response_time', 'p95_response_time'] }],
  },
  topErrorTypes: {
    definition: 'Client and server exceptions sorted by occurrence count for bug-fixing focus.',
    formula: 'GROUP BY error_type ORDER BY SUM(total_occurrences) DESC LIMIT 10',
    logic: ['Users Impacted sums unique users affected per error type.'],
    sources: [{ system: 'BigQuery', table: 'error_summary_daily', columns: ['error_type', 'total_occurrences', 'unique_users'] }],
  },
};

export const appTrackingMetricInfo: MetricInfoMap = {
  page: {
    definition: 'Install, likely uninstall, and time-spent analytics derived from analytics.events.',
    formula: 'Aggregates APP_INSTALL_FIRST_OPEN, uninstall signals, and dwell-time events over the selected range.',
    logic: [
      'Use alongside the Analytics Dashboard: that page is the traffic counter; this page is the stopwatch for attention.',
      'Likely uninstalls are inferred from FCM NotRegistered failures or prolonged inactivity.',
    ],
    sources: [{ system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'timestamp', 'screen', 'service', 'duration_ms'] }],
  },
  installations: {
    definition: 'Total new user acquisitions and first device setups in the selected date range.',
    formula: "COUNT(1) WHERE event_type = 'APP_INSTALL_FIRST_OPEN'",
    logic: ['Counts first-open install events from the events table.'],
    sources: [{ system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'timestamp'] }],
  },
  likelyUninstalls: {
    definition: 'Estimated churn from FCM push delivery failures (NotRegistered) or 30+ days of inactivity.',
    formula: 'COUNT(DISTINCT devices/users matching uninstall heuristics)',
    logic: ['Heuristic signal — not a store uninstall webhook.', 'Use as an early churn warning, not an exact App Store/Play count.'],
    sources: [{ system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'user_id', 'timestamp'] }],
  },
  netGrowth: {
    definition: 'Net install growth after subtracting likely uninstalls.',
    formula: 'install_count - uninstall_count',
    logic: ['Positive net growth means acquisitions outpaced inferred churn.'],
    sources: [{ system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'timestamp'] }],
  },
  growthChurnTrend: {
    definition: 'Daily installs, likely uninstalls, and net growth over the selected window.',
    formula: 'GROUP BY day: install_count, uninstall_count, net_growth',
    logic: ['Zero-fills missing days so gaps are visible as flat lines.'],
    sources: [{ system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'timestamp'] }],
  },
  acquisitionBar: {
    definition: 'Daily install volume as a bar series for acquisition pacing.',
    formula: 'COUNT installs GROUP BY day',
    logic: ['Compare spikes against campaigns or release dates.'],
    sources: [{ system: 'BigQuery', table: 'analytics.events', columns: ['event_type', 'timestamp'] }],
  },
  screenTimeSpent: {
    definition: 'Identifies which app screens capture the most user attention by session count and dwell time.',
    formula: 'SUM(duration_ms) & COUNT(sessions) GROUP BY screen',
    logic: ['Business utility: prioritize UX polish on high-dwell screens and investigate drop-offs on low-dwell critical flows.'],
    sources: [{ system: 'BigQuery', table: 'analytics.events', columns: ['screen', 'duration_ms', 'session_id'] }],
  },
  serviceTimeSpent: {
    definition: 'Tracks engagement across medical, ambulance, pharmacy, and other services by time spent.',
    formula: 'SUM(duration_ms) & COUNT(sessions) GROUP BY service',
    logic: ['Business utility: compare service stickiness and inform growth investment.'],
    sources: [{ system: 'BigQuery', table: 'analytics.events', columns: ['service', 'duration_ms', 'session_id'] }],
  },
};

export const activityTrackingMetricInfo: MetricInfoDefinition = {
  definition: 'Live visitor and event analysis across Website, User app, and Plus app. Anonymous website visitors get a stable visitor id (anon_web_…) so repeat guest traffic can be tracked like logged-in users.',
  formula: 'COUNT(events), COUNT(DISTINCT user_id), COUNT(DISTINCT session_id) from analytics.events',
  logic: [
    'Channel: web vs user_app vs plus_app from metadata.client_app, platform, actor_type, and screen_name.',
    'Anonymous visitors: user_id starting with anon, actor_type ANONYMOUS, or metadata.actor_origin.',
    'Pages use assigned canonical screen names (web:user:… / app:user:… / app:vendor:…).',
    'By service groups events into Ambulance, Doctor consultation (instant / online / in-clinic), Health records, Blogs, Awareness, Her phases, My orders tabs, Track order, Contact us, Grievance, Refund, Privacy, Terms, Invite friends, and Profile.',
    'Events use assigned event_type values (SCREEN_VIEW, UI_CLICK, *_OPENED, bookings, payments).',
    'Targets use target_id / entity_id (clinic, blog, button, route).',
    'Queries are limited to 90 days and aggregated in one BigQuery scan.',
  ],
  sources: [
    { system: 'BigQuery', table: 'analytics.events', columns: ['user_id', 'actor_type', 'platform', 'event_type', 'screen_name', 'target_id', 'session_id', 'timestamp'] },
  ],
};

export const herPhasesMetricInfo: MetricInfoMap = {
  page: {
    definition: 'Her Phases is Vedika’s free cycle toolkit: Period, Ovulation, Pregnancy Test, and Menstrual Cycle calculators. This hub combines saved calculations (MySQL), in-app/web events (Firebase → BigQuery), and WhatsApp retarget nudges.',
    formula: 'MySQL herPhases aggregates + BigQuery analytics.events WHERE product_key = her_phases + WhatsAppRetargetingQueues WHERE service = HER_PHASES',
    logic: [
      'Overview mixes calculator volume with unique visitors and open-to-calculate conversion.',
      'Cycle insights describe entered cycle length and period duration — not clinical diagnoses.',
      'Product activity is the same event stream as Analytics Dashboard → Activity Tracking, locked to Her Phases.',
    ],
    sources: [
      { system: 'MySQL', table: 'herPhases', columns: ['user_id', 'calculator_type', 'cycle_length', 'period_duration', 'createdAt'] },
      { system: 'BigQuery / Firebase events', table: 'analytics.events', columns: ['event_type', 'screen_name', 'user_id', 'platform', 'timestamp'] },
      { system: 'MySQL', table: 'WhatsAppRetargetingQueues', columns: ['service', 'status', 'eventType'] },
    ],
  },
  totalUsers: {
    definition: 'Distinct logged-in user IDs who saved at least one Her Phases calculation in the selected range.',
    formula: 'COUNT(DISTINCT user_id) FROM herPhases WHERE createdAt BETWEEN from AND to',
    sources: [{ system: 'MySQL', table: 'herPhases', columns: ['user_id', 'createdAt'] }],
  },
  calcsPerUser: {
    definition: 'Average saved calculations per unique user.',
    formula: 'totalCalculations / uniqueUsers',
    sources: [{ system: 'MySQL', table: 'herPhases', columns: ['herPhaseId', 'user_id'] }],
  },
  cycleLength: {
    definition: 'Mean cycle length entered into calculators. Typical-band average only includes 21–35 day cycles.',
    formula: 'AVG(cycle_length)',
    sources: [{ system: 'MySQL', table: 'herPhases', columns: ['cycle_length'] }],
  },
  periodDuration: {
    definition: 'Mean period duration entered into Period and Menstrual Cycle calculators.',
    formula: 'AVG(period_duration)',
    sources: [{ system: 'MySQL', table: 'herPhases', columns: ['period_duration'] }],
  },
  conversion: {
    definition: 'Share of Her Phases opens/visitors that resulted in a saved calculation. Uses HER_PHASES_OPENED when present, otherwise unique visitors.',
    formula: 'calculations / MAX(herPhasesOpened, unique_visitors) * 100',
    sources: [
      { system: 'MySQL', table: 'herPhases', columns: ['herPhaseId'] },
      { system: 'BigQuery', table: 'analytics.events', columns: ['event_type'] },
    ],
  },
  guests: {
    definition: 'Calculations saved without a user_id (guest / incomplete profile) versus distinct logged-in users.',
    formula: 'COUNT(*) WHERE user_id IS NULL OR user_id = \'\'',
    sources: [{ system: 'MySQL', table: 'herPhases', columns: ['user_id'] }],
  },
  repeatRate: {
    definition: 'Share of logged-in calculator users with more than one saved calculation in the range.',
    formula: 'COUNT(users with COUNT(*) > 1) / COUNT(DISTINCT user_id) * 100',
    sources: [{ system: 'MySQL', table: 'herPhases', columns: ['user_id'] }],
  },
  dwell: {
    definition: 'Average dwell time on Her Phases screens from the event stream.',
    formula: 'AVG(dwell_time_sec) WHERE product_key = her_phases',
    sources: [{ system: 'BigQuery', table: 'analytics.events', columns: ['dwell_time_sec', 'screen_name'] }],
  },
  dailyTrend: {
    definition: 'Saved calculations per day from MySQL.',
    formula: 'COUNT(*) GROUP BY DATE(createdAt)',
    sources: [{ system: 'MySQL', table: 'herPhases', columns: ['createdAt'] }],
  },
  calculatorMix: {
    definition: 'Share of Period, Ovulation, Pregnancy Test, and Menstrual Cycle calculator runs.',
    formula: 'COUNT(*) GROUP BY calculator_type',
    sources: [{ system: 'MySQL', table: 'herPhases', columns: ['calculator_type'] }],
  },
};

export const chatbotIntelligenceMetricInfo: MetricInfoMap = {
  page: {
    definition: 'Monitor bot performance, unanswered queries, intent mix, and live conversation quality.',
    formula: 'Aggregates chatbot_events over the selected lookback window.',
    logic: ['Use Success Rate and Fallbacks together to spot knowledge gaps.', 'Unanswered list feeds content / FAQ backlog.'],
    sources: [{ system: 'BigQuery', table: 'analytics.chatbot_events', columns: ['user_id', 'intent', 'response_status', 'question', 'timestamp'] }],
  },
  totalMessages: {
    definition: 'Total messages processed by the chatbot in the selected timeframe.',
    formula: 'COUNT(*)',
    sources: [{ system: 'BigQuery', table: 'analytics.chatbot_events', columns: ['*'] }],
  },
  uniqueUsers: {
    definition: 'Distinct users who interacted with the chatbot.',
    formula: 'COUNT(DISTINCT user_id)',
    sources: [{ system: 'BigQuery', table: 'analytics.chatbot_events', columns: ['user_id'] }],
  },
  successRate: {
    definition: 'Share of queries answered without falling back to a generic response.',
    formula: "(COUNTIF(status = 'answered') / COUNT(*)) * 100",
    sources: [{ system: 'BigQuery', table: 'analytics.chatbot_events', columns: ['response_status'] }],
  },
  fallbacks: {
    definition: "Queries the bot couldn't answer, triggering fallback or human handoff.",
    formula: "COUNTIF(status IN ('unanswered', 'fallback'))",
    sources: [{ system: 'BigQuery', table: 'analytics.chatbot_events', columns: ['response_status'] }],
  },
  intentDistribution: {
    definition: 'Volume of classified intents (ambulance_service, doctor_consultation, etc.) in the window.',
    formula: 'COUNT(*) GROUP BY intent ORDER BY count DESC',
    logic: ['Helps prioritize training data and FAQ coverage for high-volume intents.'],
    sources: [{ system: 'BigQuery', table: 'analytics.chatbot_events', columns: ['intent'] }],
  },
  unansweredQuestions: {
    definition: 'Recent questions that were unanswered — primary knowledge-gap backlog.',
    formula: "FILTER response_status IN ('unanswered','fallback') ORDER BY timestamp DESC",
    sources: [{ system: 'BigQuery', table: 'analytics.chatbot_events', columns: ['question', 'response_status', 'timestamp'] }],
  },
  liveConversationAudit: {
    definition: 'Recent chat turns grouped by user for quality review and hallucination checks.',
    formula: 'Latest N sessions ordered by timestamp, grouped by user_id',
    sources: [{ system: 'BigQuery', table: 'analytics.chatbot_events', columns: ['user_id', 'question', 'answer', 'intent', 'response_status', 'timestamp'] }],
  },
};

export const monitoringMetricInfo: MetricInfoMap = {
  page: {
    definition: 'Fused business and technical monitoring across GMV, revenue, vendor ops, and API health.',
    formula: 'Combines MySQL identity truth with BigQuery predictive analytics for the selected range.',
    logic: [
      'Overview: business velocity and service mix.',
      'Operations: vendor operational KPIs (orders, dispatch, commission, ratings).',
      'Technical Tower: P95 latency and error rate pulse.',
    ],
    sources: [
      { system: 'MySQL', table: 'Orders / Bookings', columns: ['status', 'amounts', 'vendorId'] },
      { system: 'BigQuery', table: 'analytics aggregates', columns: ['gmv', 'revenue', 'latency', 'error_rate'] },
    ],
  },
  ordersBookings: {
    definition: 'Count of completed orders and bookings attributed to the vendor in the window.',
    formula: 'COUNT(orders + bookings) WHERE status IN completed set',
    sources: [{ system: 'MySQL', table: 'ClinicAppointments / AmbulanceBookings / Orders', columns: ['status', 'createdAt', 'vendorId'] }],
  },
  dispatchResponse: {
    definition: 'Average time for the vendor / dispatch stack to respond to a request.',
    formula: 'AVG(dispatch_response_ms)',
    sources: [{ system: 'BigQuery / MySQL', table: 'dispatch timing events', columns: ['response_ms'] }],
  },
  platformCommission: {
    definition: 'Estimated platform commission captured from vendor transactions.',
    formula: 'SUM(commission_amount)',
    sources: [{ system: 'MySQL', table: 'settlements / orders', columns: ['commission'] }],
  },
  estImpact: {
    definition: 'Estimated business impact score combining volume, commission, and engagement signals.',
    formula: 'Weighted composite of orders, GMV contribution, and ratings',
    sources: [{ system: 'Derived', table: 'vendor intelligence', columns: ['impact_score'] }],
  },
  userRating: {
    definition: 'Average user rating for the vendor over the selected window.',
    formula: 'AVG(rating)',
    sources: [{ system: 'MySQL', table: 'Feedbacks / Reviews', columns: ['rating', 'vendorId'] }],
  },
  gmvVsRevenue: {
    definition: 'Comparison of total user spending (GMV) vs platform profit (Revenue).',
    formula: 'GMV = gross merchandise value; Revenue = platform take after costs',
    sources: [{ system: 'BigQuery', table: 'daily business aggregates', columns: ['gmv', 'revenue'] }],
  },
  p95Latency: {
    definition: 'Average time for AI and server paths to respond (P95 pulse).',
    formula: 'P95(response_time_ms)',
    sources: [{ system: 'BigQuery', table: 'api latency aggregates', columns: ['p95_ms'] }],
  },
  errorRatePulse: {
    definition: 'Real-time analysis of failed user requests in the technical tower.',
    formula: 'failed_requests / total_requests * 100',
    sources: [{ system: 'BigQuery', table: 'api error aggregates', columns: ['error_rate'] }],
  },
};

export const vendorStorageMetricInfo: MetricInfoMap = {
  totalActiveBytes: {
    definition: 'Total binary storage space consumed by all active (non-deleted) vendor files.',
    formula: 'SUM(file_size_bytes) WHERE actor_type = "VENDOR" AND latest_operation = "UPLOAD"',
    logic: ['Sums the file_size_bytes of all storage paths belonging to vendor accounts whose latest event is a successful UPLOAD.'],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['file_size_bytes', 'actor_type', 'storage_path', 'operation', 'status'] }],
  },
  totalActiveFiles: {
    definition: 'Total count of active (non-deleted) files uploaded by vendors.',
    formula: 'COUNT(storage_path) WHERE actor_type = "VENDOR" AND latest_operation = "UPLOAD"',
    logic: ['Counts all storage paths belonging to vendor accounts whose latest event is a successful UPLOAD.'],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['storage_path', 'actor_type', 'operation', 'status'] }],
  },
  totalUploadAttempts: {
    definition: 'Total number of upload operations attempted by vendors (both successful and failed).',
    formula: 'COUNT(1) WHERE actor_type = "VENDOR" AND operation = "UPLOAD"',
    logic: ['Counts all upload events in the log where the actor_type is VENDOR.'],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['operation', 'actor_type'] }],
  },
  globalSuccessRatePercent: {
    definition: 'Percentage of vendor upload attempts that completed successfully.',
    formula: 'COUNTIF(status = "SUCCESS") / COUNT(*) * 100',
    logic: ['Calculates the ratio of successful UPLOAD events to total UPLOAD events for VENDOR actor type.'],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['status', 'operation', 'actor_type'] }],
  },
  averageUploadDurationMs: {
    definition: 'Average time taken to upload vendor files to storage.',
    formula: 'AVG(duration_ms) WHERE operation = "UPLOAD" AND actor_type = "VENDOR"',
    logic: ['Aggregates the duration_ms column for all UPLOAD events where actor_type is VENDOR.'],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['duration_ms', 'operation', 'actor_type'] }],
  },
  activeActorsCount: {
    definition: 'Number of unique vendor accounts that have active files in storage.',
    formula: 'COUNT(DISTINCT actor_id) WHERE actor_type = "VENDOR" AND latest_operation = "UPLOAD"',
    logic: ['Counts unique actor_id values from all vendor storage paths whose latest event is UPLOAD and status is SUCCESS.'],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['actor_id', 'actor_type', 'storage_path', 'operation', 'status'] }],
  },
  estimatedMonthlyStorageCostUsd: {
    definition: 'Estimated monthly hosting charges for storing the active vendor files.',
    formula: 'totalActiveBytes / 1,073,741,824 * $0.026',
    logic: ['Converts total active bytes to gigabytes (GB) and multiplies by Google Cloud Storage standard regional pricing rate ($0.026/GB/month).'],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['file_size_bytes', 'actor_type', 'storage_path', 'operation', 'status'] }],
  },
  storageTypeDistribution: {
    definition: 'Donut chart visualizing the percentage distribution of storage size (in bytes) across different vendor document and asset categories.',
    formula: 'SUM(file_size_bytes) GROUP BY entity_type',
    logic: [
      'Filters for active vendor files (latest operation is a successful UPLOAD).',
      'Groups by entity_type to aggregate total bytes for each category.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['file_size_bytes', 'entity_type', 'actor_type', 'storage_path', 'operation', 'status'] }],
  },
  storageWeightsBreakdown: {
    definition: 'Detailed list showing exact file counts, storage sizes, and percentage share of total bytes for each vendor file category.',
    formula: 'COUNT(1) & SUM(file_size_bytes) GROUP BY entity_type',
    logic: [
      'Aggregates total file count and size in bytes for each unique entity_type belonging to vendors.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['file_size_bytes', 'entity_type', 'actor_type', 'storage_path', 'operation', 'status'] }],
  },
  healthTimeline: {
    definition: 'Daily trend monitoring of upload transaction volume, successes/failures, and average latency over the selected time window.',
    formula: 'COUNT(1) & AVG(duration_ms) GROUP BY DATE(event_ts)',
    logic: [
      'Groups vendor upload events by day (DATE(event_ts)).',
      'Calculates successful uploads, failed uploads, and average upload duration (latency) for each day.',
    ],
    sources: [{ system: 'BigQuery', table: 'storage_upload_events', columns: ['event_ts', 'operation', 'status', 'duration_ms', 'actor_type'] }],
  },
  leaderboard: {
    definition: 'A ranked list of all active vendors sorted by their total storage footprint, file count, or success rate.',
    formula: 'SUM(file_size_bytes) & COUNT(1) GROUP BY actor_id ORDER BY storage_bytes DESC',
    logic: [
      'Queries BigQuery to aggregate storage volume and file counts for each distinct actor_id of type VENDOR.',
      'Enriches the vendor IDs with operational metadata (email, phone, category) from the MySQL database.',
    ],
    sources: [
      { system: 'BigQuery', table: 'storage_upload_events', columns: ['actor_id', 'actor_type', 'file_size_bytes', 'operation', 'status'] },
      { system: 'MySQL', table: 'Vendor', columns: ['vendorId', 'generatedId', 'email', 'phoneNumber', 'vendorRole'] },
    ],
  },
};
